import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { workspaceAPI } from 'features/workspace/api/workspace';
import { cloneDeep } from 'lodash';
import {
  CreateRecipeAPI,
  DeleteRecipeAPI,
  DuplicateRecipeAPI,
  FetchRecipeAPI,
  FetchRecipesAPI,
  UpdateRecipeAPI,
  UploadRecipeImageAPI,
} from '../api/RecipeService';
import { deleteRecipeTask, reorderRecipeTask } from './recipeTasksSlice';

/**
 * Fetch Recipes
 * @param {Number} projectId
 */
export const fetchRecipes = createAsyncThunk(
  'recipes/fetchRecipes',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await FetchRecipesAPI();
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Fetch Recipes
 * @param {Number} projectId
 */
export const fetchRecipe = createAsyncThunk(
  'recipes/fetchRecipe',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await FetchRecipeAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Add Recipe
 * @param {*} requestParameter
 * * @param {Number} requestParameter.id
 * * @param {String} requestParameter.name
 */
export const addRecipe = createAsyncThunk(
  'recipes/addRecipe',
  async (requestParameters, { rejectWithValue }) => {
    const data = cloneDeep(requestParameters);
    data.id && delete data.id;
    const apiResponse = await CreateRecipeAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return { data: apiResponse?.response.data, removeId: requestParameters.id };
    }
  }
);

/**
 * Update Recipe
 * @param {*} requestParameter
 * * @param {Number} requestParameter.id
 * * @param {String} requestParameter.name
 */
export const updateRecipe = createAsyncThunk(
  'recipes/updateRecipe',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await UpdateRecipeAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Delete Recipe
 * @param {Number} id
 */
export const deleteRecipe = createAsyncThunk(
  'recipes/deleteRecipe',
  async (id, { rejectWithValue }) => {
    const apiResponse = await DeleteRecipeAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return id;
    }
  }
);

/**
 * Duplicate Recipe
 * @param {Number} id
 */
export const duplicateRecipe = createAsyncThunk(
  'recipes/duplicateRecipe',
  async (id, { rejectWithValue }) => {
    const apiResponse = await DuplicateRecipeAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Upload Recipe Images
 * @param {Object} formData
 * @param {String} formData.recipeId
 * @param {File} formData.file
 */
export const uploadRecipeImages = createAsyncThunk(
  'recipes/uploadRecipeImages',
  async (formData, { rejectWithValue }) => {
    formData.append('_method', 'post');
    const apiResponse = await UploadRecipeImageAPI(formData);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response?.message);
    } else {
      return apiResponse.response.data;
    }
  }
);

export const recipeAdapter = createEntityAdapter({
  sortComparer: (prev, curr) => prev?.name.localeCompare(curr?.name, undefined, { numeric: true }),
});

const initialState = recipeAdapter.getInitialState({
  error: undefined,
  recipeDrawer: { open: false },
});

const recipeSlice = createSlice({
  name: 'Recipe',
  initialState,
  reducers: {
    addEmptyRecipe: (state, action) => {
      recipeAdapter.addOne(state, {
        id: Math.random(),
        isEmpty: true,
      });
    },
    setRecipeDrawer: (state, action) => {
      state.recipeDrawer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRecipes.fulfilled, (state, action) => {
      recipeAdapter.setAll(state, action.payload?.data);
    });

    builder.addCase(fetchRecipe.fulfilled, (state, action) => {
      recipeAdapter.upsertOne(state, {
        id: action.payload.data.id,
        name: action.payload.data?.name,
        type: action.payload?.type,
      });
    });

    // Update Recipe
    builder.addCase(updateRecipe.fulfilled, (state, action) => {
      recipeAdapter.upsertOne(state, { ...action.payload.data, loading: false });
    });

    // Delete Recipe
    builder.addCase(deleteRecipe.pending, (state, action) => {
      recipeAdapter.upsertOne(state, { id: action.meta?.arg, loading: true });
    });
    builder.addCase(deleteRecipe.fulfilled, (state, action) => {
      recipeAdapter.removeOne(state, action.payload);
    });
    builder.addCase(deleteRecipe.rejected, (state, action) => {
      recipeAdapter.upsertOne(state, { id: action.meta?.arg, loading: false });
    });

    // Add Recipe
    builder.addCase(addRecipe.fulfilled, (state, action) => {
      recipeAdapter.upsertOne(state, action.payload.data);
    });

    // Reorder Top Level Recipe Tasks
    //TODO: CHANGE HOW THIS IS STRUCTURED SO WE CAN USE JUST THE RECIPE TASKS SLICE
    builder.addCase(reorderRecipeTask.fulfilled, (state, action) => {
      recipeAdapter.updateOne(state, {
        id: action.meta.arg.recipeId,
        changes: { tasks: action.payload },
      });
    });

    // Add Empty Recipe Task
    builder.addCase('recipeTasks/addEmptyRecipeTask', (state, action) => {
      const recipe = state.entities[action.payload.recipeId];
      recipe.tasks ? recipe.tasks.push(action.payload.id) : (recipe['tasks'] = [action.payload.id]);
    });

    // Delete Recipe Task
    builder.addCase(deleteRecipeTask.fulfilled, (state, action) => {
      const arg = action.meta.arg;
      if (arg.parentType === 'RECIPE') {
        const recipe = state.entities[arg.parentId];
        const taskId = recipe?.tasks?.findIndex((taskId) => taskId == arg.id);
        recipe?.tasks?.splice(taskId, 1);
      }
    });

    builder.addCase(duplicateRecipe.fulfilled, (state, action) => {
      recipeAdapter.upsertOne(state, action.payload);
    });

    builder.addMatcher(workspaceAPI.endpoints.setWorkspace.matchFulfilled, (state, action) => {
      recipeAdapter.removeAll(state);
    });
  },
});

export default recipeSlice.reducer;

/** Selectors **/

export const {
  selectById: selectRecipeById,
  selectIds: selectRecipeIds,
  selectEntities: selectRecipeEntities,
  selectAll: selectAllRecipes,
  selectTotal: selectTotalRecipes,
} = recipeAdapter.getSelectors((state) => state.recipes);

export const { addEmptyRecipe, setRecipeDrawer } = recipeSlice.actions;
